import { parseBrandPath } from '@/lib/routes'
import { safeRedirectPath } from '@/lib/safe-redirect'

export const BILLING_TASK_RETURN_PARAM = 'return_to'

/** Only return to work in the paying organization, never another checkout. */
export function billingTaskReturnPath(
  raw: string | null | undefined,
  orgSlug: string,
): string | null {
  const path = safeRedirectPath(raw)
  if (!path) return null
  const url = new URL(path, 'https://billing.invalid')
  const scope = parseBrandPath(url.pathname)
  if (scope?.orgSlug !== orgSlug) return null
  // Normalize dot segments before checking scope and reject settings/redirect
  // surfaces. A crafted return URL must not send the user into a payment loop.
  // `connect-tools` is the onboarding step whose ChatGPT Ads card opens the
  // upgrade dialog; the default plan page is unreachable until onboarding
  // finishes, so the wizard is the only place that checkout can land.
  const sub = url.pathname.split('/').slice(5).join('/')
  if (!/^(chat|creatives|dashboard|connect-tools)(\/|$)/.test(sub)) return null
  url.searchParams.delete(BILLING_TASK_RETURN_PARAM)
  return `${url.pathname}${url.search}${url.hash}`
}

/** Carry the task through a billing tab, or capture the current task directly. */
export function currentBillingTaskReturn(orgSlug: string): string | null {
  if (typeof window === 'undefined') return null
  const url = new URL(window.location.href)
  return (
    billingTaskReturnPath(url.searchParams.get(BILLING_TASK_RETURN_PARAM), orgSlug) ??
    billingTaskReturnPath(`${url.pathname}${url.search}${url.hash}`, orgSlug)
  )
}

export function withBillingTaskReturn(url: string, task: string | null): string {
  if (!task) return url
  const target = new URL(url)
  target.searchParams.set(BILLING_TASK_RETURN_PARAM, task)
  return target.href
}

const continuationKey = (org: string, user: string) =>
  `soku.task-checkout:${org}:${user}`

/** Stripe can reuse a pending checkout whose return URLs were frozen earlier. */
export function rememberBillingTaskReturn(
  org: string,
  user: string,
  path: string | null,
): void {
  try {
    const key = continuationKey(org, user)
    // A null destination deliberately overrides an older task-specific URL.
    window.sessionStorage.setItem(
      key,
      JSON.stringify({ path, expires: Date.now() + 60 * 60 * 1000 }),
    )
  } catch {
    // The URL still carries the continuation when browser storage is unavailable.
  }
}

export function readBillingTaskReturn(
  org: string,
  user: string,
): { path: string | null } | null {
  try {
    const raw = window.sessionStorage.getItem(continuationKey(org, user))
    if (!raw) return null
    const entry = JSON.parse(raw) as { path?: unknown; expires?: unknown }
    if (
      (entry.path !== null && typeof entry.path !== 'string') ||
      typeof entry.expires !== 'number' ||
      entry.expires <= Date.now()
    ) return null
    return { path: billingTaskReturnPath(entry.path, org) }
  } catch {
    return null
  }
}
